import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import './index.css';
import { App } from './components/App';
import { AuthState } from './components/Authorization';
import { getServicesManager } from './services';
import { CHROME_EXTENSION_ACTIVATE_PAGE, CHROME_EXTENSION_EVERYWHERE_ACTIVATE_PAGE, CHROME_EXTENSION_GHL_ACTIVATE_PAGE, CHROME_EXTENSION_VICI_ACTIVATE_PAGE, SUGGEST_FEATURE_PAGE } from './cfg/endpoints';
import { Typography } from './interfaces/typography';
import { Provider } from 'react-redux';
import { store} from './store';
import { refreshImportOnce } from './lib/loading';

import { SpeedInsights } from "@vercel/speed-insights/react"
import { Loader } from './components/Loader';
import { EmbeddedAutomator } from './components/Embedded';
import {PrivateRoute } from './components/Authentication/Authentication';
import { ExtensionPageWrap } from './components/ExtensionPageWrap';
import { SettingsPages } from './components/Settings';
import { LinkedInEmbedded } from './components/LinkedInEmbedded';
import { BotPortal } from './components/BotPortal/BotPortal';
import Refresh from './components/Refresh/Refresh';
import { SessionDetailView } from './components/History/views';
import { GoogleSheetsOAuth } from './components/GoogleSheets';
import { SalesforceCallback } from './components/Integrations/SalesforceCallback';
import { HubSpotCallback } from './components/Integrations/HubSpotCallback';
import { Plays } from './components/Plays';
import { PublicSessionView } from './components/PublicSessionView';

const History = React.lazy(()=> refreshImportOnce(import('./components/History')).then(module=>({default:module.History})))
const Coaching = React.lazy(()=> refreshImportOnce(import('./components/Coaching')).then(module=>({default:module.Coaching})))
const Install = React.lazy(()=> refreshImportOnce(import('./components/Install')).then(module=>({default:module.Install})))
const Authentication = React.lazy(()=> refreshImportOnce(import('./components/Authorization')).then(module=>({default:module.Authentication})))
const Uninstall = React.lazy(()=> refreshImportOnce(import('./components/Uninstall')).then(module=>({default:module.Uninstall})))
const Redirect = React.lazy(()=> refreshImportOnce(import('./components/Redirect')).then(module=>({default:module.Redirect})))
const Demo = React.lazy(()=> refreshImportOnce(import('./components/Demo')).then(module=>({default:module.Demo})))
const Support = React.lazy(()=> refreshImportOnce(import('./components/Support')).then(module=>({default:module.Support})))
const LeaderboardTeamCreation = React.lazy(()=> refreshImportOnce(import('./components/LeaderboardTeamCreation')).then(module=>({default:module.LeaderboardTeamCreation})))
const AutoDialerList = React.lazy(()=> refreshImportOnce(import('./components/AutoDialerList')).then(module=>({default:module.AutoDialerList})))
const PaymentSuccess = React.lazy(()=> refreshImportOnce(import('./components/PaymentSuccess')).then(module=>({default:module.PaymentSuccess})))
const InternalTools = React.lazy(()=> refreshImportOnce(import('./components/InternalTools')).then(module=>({default:module.InternalTools})))
const EmbeddedIframe = React.lazy(()=> refreshImportOnce(import('./components/EmbeddedIframe')).then(module=>({default:module.EmbeddedIframe})))
const MultiDialerAdminV2 = React.lazy(()=> refreshImportOnce(import('./components/MultiDialerAdminV2')).then(module=>({default:module.MultiDialerAdminV2})))
const SynthProspect = React.lazy(()=> refreshImportOnce(import('./components/SynthProspect')).then(module=>({default:module.SynthProspect})))
const Analytics = React.lazy(()=> refreshImportOnce(import('./components/Analytics')).then(module=>({default:module.AnalyticsV2})))
const Settings = React.lazy(()=> refreshImportOnce(import('./components/Settings')).then(module=>({default:module.Settings})))
const Live = React.lazy(()=> refreshImportOnce(import('./components/Live')).then(module=>({default:module.Live})))
const LiveRelay = React.lazy(()=> refreshImportOnce(import('./components/LiveLeaderboard')).then(module=>({default:module.LiveRelay})))
const LinkedIn = React.lazy(()=> refreshImportOnce(import('./components/LinkedIn')).then(module=>({default:module.LinkedIn})))
const SalesFloorRoomJoin = React.lazy(()=> refreshImportOnce(import('./components/SalesFloorRoomJoin/SalesFloorRoomJoin')).then(module=>({default:module.SalesFloorRoomJoin})))
const Invitations = React.lazy(()=> refreshImportOnce(import('./components/Invitations')).then(module=>({default:module.Invitations})))
const Permission = React.lazy(()=> refreshImportOnce(import('./components/Permission/Permission')).then(module=>({default:module.Permission})))
// Add Law Firm component import
const LawFirm = React.lazy(()=> refreshImportOnce(import('./components/LawFirm/DemoUsage')).then(module=>({default:module.default})))
// Add Configuration component import
const Configuration = React.lazy(()=> refreshImportOnce(import('./components/Configuration')).then(module=>({default:module.Configuration})))
// Add Users component import
const Users = React.lazy(()=> refreshImportOnce(import('./components/Users')).then(module=>({default:module.Users})))
// Add Interview component import
const Interview = React.lazy(()=> refreshImportOnce(import('./components/Interview')).then(module=>({default:module.Interview})))
// Add OnboardingGuide component import
const OnboardingGuide = React.lazy(()=> refreshImportOnce(import('./components/OnboardingGuide')).then(module=>({default:module.OnboardingGuide})))
// Add CaptureNow component import
const CaptureNow = React.lazy(()=> refreshImportOnce(import('./components/CaptureNow')).then(module=>({default:module.CaptureNow})))
// ensure the service manager is started
const serviceManager = getServicesManager() 
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

function wrap(x: any) {
  return <React.Suspense fallback={<div><Loader /></div>}>
    {x}
  </React.Suspense>
}

root.render(
  <React.StrictMode>
    <SpeedInsights />
    <Provider store={store}>
    <BrowserRouter>
    <Routes>
      <Route path="/" element={<PrivateRoute component={App}/>}>
        <Route index element={<PrivateRoute component={Analytics}/>} />
        <Route path="analytics" element={<PrivateRoute component={Analytics}/>} />
        <Route path="transcripts" element={<PrivateRoute component={() => <History view={SessionDetailView.MAIN} />}/>} />
        <Route path="transcripts/:id" element={<PrivateRoute component={() => <History view={SessionDetailView.MAIN} />}/>} />

        <Route path="plays" element={<PrivateRoute component={Plays}/>} />
        <Route path="scripts" element={<PrivateRoute component={Plays}/>} />
        <Route path="settings" element={<PrivateRoute component={Settings}/>} />
        <Route path="settings/integrations/salesforce/callback" element={<PrivateRoute component={SalesforceCallback}/>} />
        <Route path="settings/integrations/hubspot/callback" element={<PrivateRoute component={HubSpotCallback}/>} />
        <Route path='autodialer-settings' element={<PrivateRoute component={() => <Settings defaultSettings={SettingsPages.AUTODIALER_SETTINGS}/>}/>} />
        <Route path="subteams" element={<PrivateRoute component={() => <Settings defaultSettings={SettingsPages.TEAM_MANAGEMENT}/>}/>} />
        <Route path="client-accounts" element={<PrivateRoute component={Settings}/>} />
        <Route path="user-management" element={<PrivateRoute component={() => <Settings defaultSettings={SettingsPages.TEAM_MANAGEMENT}/>}/>} />
        <Route path="email-settings" element={<PrivateRoute component={Settings} />} />
        <Route path="voicemail-recordings" element={<PrivateRoute component={() => <Settings defaultSettings={SettingsPages.VOICEMAILS}/>}/>} />
        <Route path="number-registration" element={<PrivateRoute component={() => <Settings defaultSettings={SettingsPages.NUMBERS}/>}/>} />

        <Route path="live" element={<PrivateRoute component={Live} />} />
        <Route path="leaderboard" element={<PrivateRoute component={LiveRelay} />} />

        <Route path="multidialer-admin-v2" element={<PrivateRoute component={MultiDialerAdminV2} />} />
        <Route path="practice" element={<PrivateRoute component={SynthProspect} />} />
        <Route path="linkedin" element={<PrivateRoute component={LinkedIn} />} />
        <Route path="users" element={<PrivateRoute component={Users} />} />
        <Route path="configuration" element={<PrivateRoute component={Configuration} />} />
        <Route path="support" element={<PrivateRoute component={Support} />} />
        <Route path="invite-room" element={<PrivateRoute component={SalesFloorRoomJoin} />} />
        <Route path="invitations" element={<PrivateRoute component={Invitations} />} />
        <Route path="onboarding" element={<PrivateRoute component={OnboardingGuide} />} />
        <Route
          path="*"
          element={
            <main style={{ padding: "1rem" }}>
              <div className='w-full h-full flex flex-row items-center justify-center text-center'>
              <Typography variant='largeParagraph'>Incorrect link - Select a tab</Typography>
              </div>
            </main>
          }
        />
      </Route>
      {/** anything dependent on the chrome extension content script has to be in pages for now*/}
      <Route path="/pages"> 
        <Route path="live" element={wrap(<ExtensionPageWrap component={Coaching} />)} />
        <Route path="live/index.html" element={wrap(<ExtensionPageWrap component={Coaching} />)} />
        <Route path="iframe" element={wrap(<ExtensionPageWrap component={EmbeddedIframe} onlyProgressOnAuthenticationLoad={true} onlyLoadAuthenticationInfo={true} />)} />
        <Route path="iframe/index.html" element={wrap(<ExtensionPageWrap component={EmbeddedIframe} onlyProgressOnAuthenticationLoad={true} onlyLoadAuthenticationInfo={true} />)} />
        <Route path="login" element={wrap(<Authentication />)} />
        <Route path="reset" element={wrap(<Authentication viewState={AuthState.PASSWORD_RESET} />)} />
        <Route path="signin" element={wrap(<Authentication />)} />
        <Route path="signin/index.html" element={wrap(<Authentication />)} />
        <Route path='signup' element={wrap(<Authentication />)} />   
        <Route path="upload" element={wrap(<ExtensionPageWrap component={AutoDialerList} />)} />
        <Route path="superhuman" element={wrap(<ExtensionPageWrap component={LinkedInEmbedded} />)} />
        <Route path="sheets-integration" element={wrap(<GoogleSheetsOAuth />)} />
      </Route>

      <Route path="bot-portal" element={<BotPortal />} />
      <Route path="bot-portal/:botId" element={<BotPortal />} />
      <Route path="interview/:portalId" element={wrap(<Interview />)} />
      <Route path="interview/" element={wrap(<Interview />)} />
      <Route path="refresh" element={wrap(<Refresh />)} />
      <Route path="view" element={wrap(<PublicSessionView />)} />

      <Route path='permission' element={wrap(<Permission />)} />
      <Route path="reset" element={wrap(<Authentication viewState={AuthState.PASSWORD_RESET} viaMainPage={true} />)} />
      <Route path='login' element={wrap(<Authentication viaMainPage={true} />)} />
      <Route path='join' element={wrap(<LeaderboardTeamCreation />)} />   
      <Route path="/uninstall" element={wrap(<Uninstall />)} />
      <Route path="/install" element={wrap(<Install />)} />
      <Route path='/free' element={wrap(<Install />)} />
      <Route path="/install-now" element={wrap(<Install />)} />
      <Route path="/get-started" element={wrap(<Install />)} />
      <Route path="/try-trellus" element={wrap(<Install />)} />
      <Route path="/get-trellus" element={wrap(<Install />)} />
      <Route path="/try-now" element={wrap(<Install />)} />
      <Route path="/integrate" element={wrap(<Redirect url={CHROME_EXTENSION_ACTIVATE_PAGE}/>)} />
      <Route path="/upgrade" element={wrap(<Redirect url={CHROME_EXTENSION_ACTIVATE_PAGE}/>)} />
      <Route path="/upgrade_ghl" element={wrap(<Redirect url={CHROME_EXTENSION_GHL_ACTIVATE_PAGE}/>)} />
      <Route path="/activate_vici" element={wrap(<Redirect url={CHROME_EXTENSION_VICI_ACTIVATE_PAGE}/>)} />
      <Route path="/everywhere" element={wrap(<Redirect url={CHROME_EXTENSION_EVERYWHERE_ACTIVATE_PAGE}/>)} />
      <Route path="/suggest-feature" element={wrap(<Redirect url={SUGGEST_FEATURE_PAGE}/>)} />
      <Route path="/demo" element={wrap(<Demo />)} />
      <Route path="/success" element={wrap(<PaymentSuccess />)} />

      {/* Add Law Firm page route */}
      <Route path="/law-firm" element={wrap(<LawFirm />)} />

      {/* Add CaptureNow page route */}
      <Route path="/capturenow" element={wrap(<CaptureNow />)} />

      <Route path="/learn" element={wrap(<Install />)} />
      <Route path="/learn-now" element={wrap(<Install />)} />
      <Route path="/learn-more" element={wrap(<Install />)} />
      <Route path="/learn-about" element={wrap(<Install />)} />

      <Route path="/internal" element={wrap(<InternalTools />)} />
      <Route path="/email" element={wrap(<EmbeddedAutomator />)} />
    </Routes>
    </BrowserRouter>
    </Provider>
    </React.StrictMode>
);
